/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Component } from 'react'

import type { DeploymentTemplateInfoV2 } from '@modules/cloud-api/v1/types'
import type { StackDeployment } from '@modules/ui-types'
import { getFilterQueryString, setFilterQueryString } from '@modules/cui/FilterContext'
import history from '@modules/utils/history'

import { isSliderSupportedForDeployment } from '@/lib/stackDeployments/selectors/deploymentTemplates'
import { isSliderInstanceType, getSupportedSliderInstanceTypes } from '@/lib/sliders/support'

import { deploymentActivityUrl, sliderActivityUrl } from '../../lib/urlBuilder'

import StackDeploymentActivityFilterableTabs from './StackDeploymentActivityFilterableTabs'

import type { WithStackDeploymentRouteParamsProps } from '../StackDeploymentEditor'
import type { ActivityTabId } from './StackDeploymentActivityTabs'

type Props = WithStackDeploymentRouteParamsProps & {
  stackDeployment: StackDeployment
  deploymentTemplate?: DeploymentTemplateInfoV2 | null
  regionId: string
  selectedTabUrl: string
  downloadActivityFeature: boolean
}

class StackDeploymentActivity extends Component<Props> {
  render() {
    const { stackDeployment, downloadActivityFeature, selectedTabUrl } = this.props

    return (
      <StackDeploymentActivityFilterableTabs
        deployment={stackDeployment}
        selectedTab={getActivityTabFromUrl(stackDeployment.id, selectedTabUrl)}
        onTabSelect={setUrlFromActivityTab(stackDeployment.id)}
        initialQuery={getFilterQueryString()}
        onQueryChange={setFilterQueryString}
        showTab={this.showTab}
        downloadActivityFeature={downloadActivityFeature}
      />
    )
  }

  showTab = (tab: ActivityTabId): boolean => {
    const { stackDeployment: deployment, deploymentTemplate } = this.props

    if (isSliderInstanceType(tab)) {
      return isSliderSupportedForDeployment({
        deployment,
        deploymentTemplate: deploymentTemplate?.deployment_template,
        sliderInstanceType: tab,
      })
    }

    return true
  }
}

function getActivityTabFromUrl(id: string, url): ActivityTabId {
  const normalizedUrl = normalizeUrl(url)
  const tabs = getSupportedSliderInstanceTypes()

  for (const tab of tabs) {
    if (buildUrl(id, tab) === normalizedUrl) {
      return tab
    }
  }

  return `_all`
}

function setUrlFromActivityTab(id: string) {
  return (tab: ActivityTabId) => {
    history.replace(buildUrl(id, tab))
  }
}

function normalizeUrl(url) {
  const firstIndexOfQuery = url.indexOf(`?`)
  return url.slice(0, firstIndexOfQuery === -1 ? Infinity : firstIndexOfQuery)
}

function buildUrl(id: string, tab: ActivityTabId) {
  if (isSliderInstanceType(tab)) {
    return sliderActivityUrl(id, tab)
  }

  return deploymentActivityUrl(id)
}

export default StackDeploymentActivity
